import { Check, Search, X } from "lucide-react";
import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  CellConfigSections,
  DetailRow,
  LabeledSelect,
  SearchableFilter,
  Section,
  shaOptions,
} from "@/components/ci/matrix-shared";
import { MatrixTabs } from "@/components/ci/matrix-tabs";
import { KpiTile } from "@/components/ci/overview-cells";
import { CiStatusBadge } from "@/components/ci/status-badge";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useTimezone } from "@/contexts/timezone-context";
import { useCryodBuildRevisions, useCryodTofa, useCryodVersions } from "@/hooks/use-ci";
import { usePageTitle } from "@/hooks/use-page-title";
import { useSearchParamSetter } from "@/hooks/use-search-param";
import type { CryodTofaImage, CryodTofaRecipe } from "@/lib/types";
import { cn, formatBytes, formatMs } from "@/lib/utils";

const ALL = "all";
const ENGINE_LABELS: Record<string, string> = { vllm: "vLLM", sglang: "SGLang" };
const TIER_ORDER = ["h100", "h200", "b200", "b300"];
// A recipe's four images, as the API lists them: the upstream release + elastic/<X.Y.Z>/main, the
// upstream release + the newest open patch-manager MR, and the route's prod image + each.
const IMAGE_LABELS = [
  "elastic main",
  "patch manager",
  "elastic main + tofa",
  "patch manager + tofa",
];

// An image's status: its latest result, or why it has none.
type ImageStatus = "pass" | "fail" | "xfail" | "skip" | "no-image" | "no-run";

// Same palette as the coverage matrix status chips, plus known issue / skip / no image.
const STATUS_STYLES: Record<ImageStatus, string> = {
  pass: "bg-emerald-500/15 text-emerald-700 ring-emerald-500/40 hover:bg-emerald-500/30 dark:text-emerald-300 dark:ring-emerald-400/50",
  fail: "bg-rose-500/20 text-rose-700 ring-rose-500/50 hover:bg-rose-500/35 dark:text-rose-300 dark:ring-rose-400/60",
  xfail:
    "bg-yellow-500/15 text-yellow-700 ring-yellow-500/40 hover:bg-yellow-500/30 dark:text-yellow-300",
  skip: "bg-slate-500/15 text-slate-600 ring-slate-500/30 hover:bg-slate-500/30 dark:text-slate-300",
  "no-image": "bg-transparent text-muted-foreground/60 ring-border hover:bg-muted/40 line-through",
  "no-run": "bg-transparent text-muted-foreground/50 ring-border hover:bg-muted/40",
};

const STATUS_LABELS: Record<ImageStatus, string> = {
  pass: "pass",
  fail: "fail",
  xfail: "known issue",
  skip: "skipped",
  "no-image": "no image",
  "no-run": "no runs",
};

const STATUS_ICONS: Partial<Record<ImageStatus, typeof Check>> = { pass: Check, fail: X };

export function imageStatus(image: CryodTofaImage): ImageStatus {
  const latest = image.latest;
  // A result on an image that is gone since says nothing about the tag now.
  if (image.image_missing) return "no-image";
  if (!latest) return "no-run";
  if (latest.status === "pass" || latest.status === "xpass") return "pass";
  if (latest.status === "fail" || latest.status === "error") return "fail";
  if (latest.status === "xfail") return "xfail";
  return "skip";
}

export function imageKey(recipe: CryodTofaRecipe, image: CryodTofaImage): string {
  return `${recipe.name}@${image.engine_version}`;
}

export function CiTofaPage() {
  usePageTitle("Token Factory Recipes");
  const [searchParams] = useSearchParams();
  const setParam = useSearchParamSetter();
  // Unlike the Catalog page, no tag means every build: recipes pin SDK images, not a cryod tag.
  const tag = searchParams.get("tag");
  const revision = searchParams.get("rev");
  const tofaQuery = useCryodTofa(tag, revision);
  const versionsQuery = useCryodVersions(revision);
  const buildRevisionsQuery = useCryodBuildRevisions(tag);
  const tagOptions = useMemo(() => {
    const versions = (versionsQuery.data ?? []).map((option) => option.version);
    // A deep-linked tag that aged out of the list stays selectable so it can be cleared.
    if (tag && !versions.includes(tag)) versions.unshift(tag);
    return versions;
  }, [versionsQuery.data, tag]);
  const cryodShaOptions = useMemo(
    () => shaOptions(buildRevisionsQuery.data?.cryod, revision),
    [buildRevisionsQuery.data, revision],
  );

  const engine = searchParams.get("engine") ?? ALL;
  const gpu = searchParams.get("gpu") ?? ALL;
  const search = searchParams.get("q") ?? "";
  const selectedKey = searchParams.get("sel");

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    // The API lists the routes most-used first.
    return (tofaQuery.data ?? []).filter(
      (recipe) =>
        (engine === ALL || recipe.engine === engine) &&
        (gpu === ALL || recipe.gpu_type === gpu) &&
        (!needle ||
          `${recipe.route} ${recipe.model_id} ${recipe.name}`.toLowerCase().includes(needle)),
    );
  }, [tofaQuery.data, engine, gpu, search]);

  const counts = useMemo(() => {
    const byStatus = new Map<ImageStatus, number>();
    for (const recipe of rows) {
      for (const image of recipe.images) {
        const status = imageStatus(image);
        byStatus.set(status, (byStatus.get(status) ?? 0) + 1);
      }
    }
    return byStatus;
  }, [rows]);

  const selected = useMemo(() => {
    for (const recipe of tofaQuery.data ?? []) {
      const image = recipe.images.find((candidate) => imageKey(recipe, candidate) === selectedKey);
      if (image) return { recipe, image };
    }
    return null;
  }, [tofaQuery.data, selectedKey]);

  const headCell =
    "sticky top-0 z-20 border-b border-r border-border/60 bg-background px-2 py-1.5 text-center text-[11px] font-medium uppercase tracking-wider text-muted-foreground";
  const modelCell =
    "sticky left-0 z-10 border-b border-r border-border/60 bg-background px-3 py-1.5 font-mono text-xs text-foreground whitespace-nowrap";
  const imageCell = "border-b border-r border-border/60 px-1.5 py-1 text-center align-middle";
  const infoCell =
    "border-b border-r border-border/60 px-2 py-1.5 text-center font-mono text-xs text-muted-foreground whitespace-nowrap";

  return (
    <div className="mx-auto max-w-screen-2xl space-y-4 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Coverage Matrix</h1>
          <p className="text-sm text-muted-foreground">
            Token Factory's prod recipes (the <code>tofa</code> tier), the routes with the largest
            share of Token Factory usage first, on four images: <em>elastic main</em> (the upstream
            release with <code>elastic/&lt;version&gt;/main</code>), <em>patch manager</em> (the
            upstream release with the newest open patch-manager MR), and{" "}
            <em>elastic main + tofa</em> and <em>patch manager + tofa</em> (Token Factory's prod
            image with that same patch set). The upstream images run only when neither tofa image
            exists; the patch-manager images only while an MR is open. A tile shows its latest
            result; click it for the full config and the run's checkpoint and restore times.
          </p>
        </div>
        <MatrixTabs current="/ci/matrix/tofa" />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <LabeledSelect
          label="Engine"
          value={engine}
          options={[ALL, "vllm", "sglang"]}
          onChange={(value) => setParam("engine", value === ALL ? "" : value)}
          renderLabel={(value) => (value === ALL ? "All engines" : (ENGINE_LABELS[value] ?? value))}
        />
        <LabeledSelect
          label="GPU"
          value={gpu}
          options={[ALL, ...TIER_ORDER]}
          onChange={(value) => setParam("gpu", value === ALL ? "" : value)}
          renderLabel={(value) => (value === ALL ? "All GPUs" : value)}
        />
        <SearchableFilter
          label="Tag"
          value={tag}
          options={tagOptions}
          noneLabel="All tags"
          noun="tag"
          onChange={(value) => setParam("tag", value)}
        />
        <SearchableFilter
          label="Cryod sha"
          value={revision}
          options={cryodShaOptions}
          onChange={(value) => setParam("rev", value)}
        />
        <div className="relative ml-auto max-w-xs flex-1">
          <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setParam("q", event.target.value, { replace: true })}
            placeholder="Filter route or model…"
            className="h-8 pl-8"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-7">
        <KpiTile label="Routes" value={rows.length} />
        <KpiTile label="Passing" value={counts.get("pass") ?? 0} />
        <KpiTile label="Failing" value={counts.get("fail") ?? 0} />
        <KpiTile label="Known issue" value={counts.get("xfail") ?? 0} />
        <KpiTile label="Skipped" value={counts.get("skip") ?? 0} />
        <KpiTile label="No image" value={counts.get("no-image") ?? 0} />
        <KpiTile label="No runs" value={counts.get("no-run") ?? 0} />
      </div>

      <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
        <span className="font-medium uppercase tracking-wider">Latest result</span>
        {(Object.keys(STATUS_LABELS) as ImageStatus[]).map((status) => {
          const StatusIcon = STATUS_ICONS[status];
          return (
            <span key={status} className="inline-flex items-center gap-1.5">
              <span
                className={cn(
                  "inline-flex size-3.5 items-center justify-center rounded-sm ring-1",
                  STATUS_STYLES[status],
                )}
              >
                {StatusIcon && <StatusIcon className="size-2.5" strokeWidth={3} />}
              </span>
              {STATUS_LABELS[status]}
            </span>
          );
        })}
      </div>

      {tofaQuery.isLoading ? (
        <div className="space-y-2">
          {["a", "b", "c", "d", "e", "f"].map((key) => (
            <Skeleton key={key} className="h-8 w-full" />
          ))}
        </div>
      ) : tofaQuery.isError ? (
        <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">
          Could not load the Token Factory recipes: {tofaQuery.error.message}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">
          No recipes match the current filters, or none are stored yet.
        </div>
      ) : (
        <div className="max-h-[calc(100vh-14rem)] overflow-auto rounded-lg border border-border">
          <table className="border-separate border-spacing-0 text-sm">
            <thead>
              <tr>
                <th className={cn(headCell, "left-0 z-30 min-w-[15rem] text-left")}>Model</th>
                <th className={headCell}>Engine</th>
                <th className={headCell}>Share</th>
                {IMAGE_LABELS.map((label) => (
                  <th key={label} className={cn(headCell, "min-w-[10rem]")}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((recipe) => (
                <tr key={recipe.name} className="hover:bg-muted/20">
                  <td className={modelCell} title={`${recipe.route} (${recipe.name})`}>
                    {recipe.route}
                  </td>
                  <td className={infoCell}>
                    {ENGINE_LABELS[recipe.engine] ?? recipe.engine} {recipe.version}
                  </td>
                  <td className={infoCell}>{recipe.share != null ? `${recipe.share}%` : "—"}</td>
                  {IMAGE_LABELS.map((label) => {
                    const image = recipe.images.find((candidate) => candidate.label === label);
                    return (
                      <td key={label} className={imageCell}>
                        {image && (
                          <ImageChip
                            recipe={recipe}
                            image={image}
                            selected={imageKey(recipe, image) === selectedKey}
                            onSelect={() => setParam("sel", imageKey(recipe, image))}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Sheet
        open={selected != null}
        onOpenChange={(open) => {
          if (!open) setParam("sel", "");
        }}
      >
        <SheetContent side="right" className="w-full gap-0 p-0 sm:max-w-2xl">
          {selected && <ImageDetail recipe={selected.recipe} image={selected.image} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function ImageChip({
  recipe,
  image,
  selected,
  onSelect,
}: {
  recipe: CryodTofaRecipe;
  image: CryodTofaImage;
  selected: boolean;
  onSelect: () => void;
}) {
  const status = imageStatus(image);
  const StatusIcon = STATUS_ICONS[status];
  return (
    <button
      type="button"
      onClick={onSelect}
      title={`${image.engine_version} — ${STATUS_LABELS[status]}${image.note ? `: ${image.note}` : ""}`}
      className={cn(
        "inline-flex items-center rounded-md px-1.5 py-0.5 font-mono text-[10px] ring-1 transition-colors",
        STATUS_STYLES[status],
        selected && "ring-2 ring-offset-1 ring-offset-background",
      )}
    >
      {StatusIcon && <StatusIcon className="mr-0.5 size-2.5 shrink-0" strokeWidth={3} />}
      {recipe.gpu_type} · {STATUS_LABELS[status]}
    </button>
  );
}

function ImageDetail({ recipe, image }: { recipe: CryodTofaRecipe; image: CryodTofaImage }) {
  const { formatTimestamp } = useTimezone();
  const latest = image.latest;
  const status = imageStatus(image);
  const results = recipe.results.filter((result) => result.engine_version === image.engine_version);
  return (
    <>
      <SheetHeader className="border-b border-border">
        <SheetTitle className="font-mono text-sm">{recipe.route}</SheetTitle>
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          <Badge variant="secondary" className="font-mono text-[10px]">
            {recipe.gpu_count}× {recipe.gpu_type}
          </Badge>
          <Badge variant="outline" className="text-[10px]">
            {ENGINE_LABELS[recipe.engine] ?? recipe.engine} {recipe.version}
          </Badge>
          <Badge variant="outline" className="text-[10px]">
            {image.label}
          </Badge>
          <Badge variant="outline" className="font-mono text-[10px]">
            {image.engine_version}
          </Badge>
        </div>
      </SheetHeader>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <Section
          title={status === "no-image" && latest ? "Last result (image gone)" : "Latest result"}
        >
          {status === "no-image" && (
            <div className="space-y-1 text-xs text-muted-foreground">
              <div>
                {latest
                  ? `The latest nightly found no image ${image.image}; this result is from an earlier run.`
                  : `No runs: the latest nightly found no image ${image.image}.`}
              </div>
              {image.note && <div>Why: {image.note}</div>}
            </div>
          )}
          {latest ? (
            <>
              <DetailRow label="Status" value={<CiStatusBadge status={latest.status} />} />
              <DetailRow
                label="CI run"
                value={
                  <Link
                    to={`/ci/${encodeURIComponent(latest.run_id)}`}
                    className="font-mono text-xs underline"
                  >
                    {latest.run_id}
                  </Link>
                }
              />
              <DetailRow
                label="Test result"
                value={
                  <Link
                    to={`/ci/${encodeURIComponent(latest.run_id)}/tests/${latest.result_id}`}
                    className="text-xs underline"
                  >
                    Logs and phases
                  </Link>
                }
              />
              <DetailRow
                label="Started"
                value={latest.started_at ? formatTimestamp(latest.started_at) : "—"}
              />
              {latest.known_issue_id && (
                <DetailRow label="Known issue" value={latest.known_issue_id} />
              )}
              {latest.skip_reason && (
                <DetailRow
                  label="Skip reason"
                  value={<span className="text-xs">{latest.skip_reason}</span>}
                />
              )}
              {latest.error && (
                <DetailRow
                  label={latest.failure_phase ? `Failed at ${latest.failure_phase}` : "Error"}
                  value={<span className="text-xs">{latest.error}</span>}
                />
              )}
              <DetailRow label="CRIU dump" value={formatMs(latest.criu_dump_ms)} />
              <DetailRow label="Checkpoint" value={formatBytes(latest.checkpoint_size_bytes)} />
              <DetailRow label="CRIU restore" value={formatMs(latest.criu_restore_ms)} />
              <DetailRow label="Total restore" value={formatMs(latest.total_restore_ms)} />
            </>
          ) : (
            status !== "no-image" && (
              <div className="space-y-1 text-xs text-muted-foreground">
                <div>{`No current result on ${image.engine_version}.`}</div>
                {image.note && <div>Why: {image.note}</div>}
              </div>
            )
          )}
        </Section>

        {results.length > 0 && (
          <Section title="Recent runs">
            <div className="flex flex-col gap-1">
              {results.map((result) => (
                <Link
                  key={result.result_id}
                  to={`/ci/${encodeURIComponent(result.run_id)}/tests/${result.result_id}`}
                  className="flex items-center gap-2 rounded px-2 py-1 ring-1 ring-border transition-colors hover:bg-muted/50"
                >
                  <CiStatusBadge status={result.status} />
                  <span className="font-mono text-[11px] text-muted-foreground">
                    {result.started_at ? formatTimestamp(result.started_at) : "—"}
                  </span>
                  <span className="ml-auto truncate font-mono text-[11px] text-muted-foreground">
                    {result.run_id}
                  </span>
                </Link>
              ))}
            </div>
          </Section>
        )}

        <Section title="Recipe">
          <DetailRow
            label="Test name"
            value={<span className="font-mono text-xs">{recipe.name}</span>}
          />
          <DetailRow
            label="Image"
            value={<span className="font-mono text-xs">{image.image}</span>}
          />
        </Section>

        <CellConfigSections config={recipe.full_config} />
      </div>
    </>
  );
}
